import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import {
    FiAlertCircle,
    FiArrowLeft,
    FiCheckCircle,
    FiDownload,
    FiEye,
    FiFileText,
    FiUpload,
    FiX
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { getApiUrl } from '../../config/api.config';

const BulkUpload = () => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('idle'); // idle, parsing, uploading, success, error
  const [uploadResults, setUploadResults] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef();

  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Check file type
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
        toast.error('Please upload a valid Excel or CSV file (.xlsx, .xls, or .csv)');
        return;
      }

      setFile(selectedFile);
      setUploadStatus('parsing');
      setUploadResults(null);
      setParsedData([]);

      try {
        // Read the file
        const data = await selectedFile.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length === 0) {
          toast.error('The file is empty or has no data');
          setUploadStatus('idle');
          return;
        }

        // Validate and parse data
        const validatedData = validateAndParseData(jsonData);
        setParsedData(validatedData);
        setShowPreview(true);
        setUploadStatus('idle');
        toast.success(`Successfully parsed ${validatedData.filter(d => d.valid).length} valid rows`);
      } catch (error) {
        console.error('Error parsing file:', error);
        toast.error('Failed to parse file. Please check the format.');
        setUploadStatus('idle');
        setFile(null);
      }
    }
  };

  const validateAndParseData = (data) => {
    return data.map((row, index) => {
      const errors = [];
      
      // Required field validation
      if (!row.Brand || row.Brand.trim() === '') errors.push('Brand is required');
      if (!row.Model || row.Model.trim() === '') errors.push('Model is required');
      if (!row.Year || isNaN(row.Year)) errors.push('Valid Year is required');
      if (!row.Price || isNaN(row.Price)) errors.push('Valid Price is required');
      if (!row.FuelType || row.FuelType.trim() === '') errors.push('Fuel Type is required');
      if (!row.Transmission || row.Transmission.trim() === '') errors.push('Transmission is required');
      
      // Year validation
      const year = parseInt(row.Year);
      const currentYear = new Date().getFullYear();
      if (year < 1990 || year > currentYear) {
        errors.push(`Year must be between 1990 and ${currentYear}`);
      }

      // Price validation
      const price = parseFloat(row.Price);
      if (price <= 0) {
        errors.push('Price must be greater than 0');
      }

      return {
        rowNumber: index + 2, // +2 because Excel rows start at 1 and first row is header
        brand: row.Brand || '',
        model: row.Model || '',
        year: row.Year || '',
        price: row.Price || '',
        fuelType: row.FuelType || '',
        transmission: row.Transmission || '',
        kmDriven: row.KMDriven || row['KM Driven'] || 0,
        mileage: row.Mileage || 0,
        location: row.Location || '',
        description: row.Description || '',
        seller: row.Seller || row.SellerName || '',
        color: row.Color || '',
        features: row.Features ? row.Features.split(',').map(f => f.trim()).filter(f => f) : [],
        valid: errors.length === 0,
        errors: errors
      };
    });
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    const validRows = parsedData.filter(row => row.valid);
    if (validRows.length === 0) {
      toast.error('No valid rows to upload. Please fix the errors and try again.');
      return;
    }

    setUploadStatus('uploading');

    try {
      // Get auth token
      const token = localStorage.getItem('agent_token');
      if (!token) {
        toast.error('Please login first');
        setUploadStatus('idle');
        return;
      }

      const results = {
        total: parsedData.length,
        successful: 0,
        failed: 0,
        errors: []
      };

      // Upload each valid car
      for (const row of validRows) {
        try {
          const carData = {
            title: `${row.brand} ${row.model}`,
            brand: row.brand,
            model: row.model,
            year: parseInt(row.year),
            price: parseFloat(row.price),
            fuelType: row.fuelType,
            transmission: row.transmission,
            kmDriven: parseInt(row.kmDriven) || 0,
            mileage: parseFloat(row.mileage) || 0,
            color: row.color || '',
            location: {
              city: row.location.split(',')[0]?.trim() || 'Unknown',
              state: row.location.split(',')[1]?.trim() || 'Unknown'
            },
            description: row.description || `${row.brand} ${row.model} - ${row.year}`,
            features: row.features,
            images: [],
            ownerType: 'First Owner',
            negotiable: true,
            condition: 'Good',
            accidentHistory: false,
            floodAffected: false,
            documents: {
              rcAvailable: true,
              insuranceValid: true,
              pucValid: true,
              serviceHistory: false
            },
            testDriveAvailable: true
          };

          const response = await fetch(getApiUrl('/cars'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(carData)
          });

          const result = await response.json();

          if (response.ok && result.success) {
            results.successful++;
          } else {
            results.failed++;
            results.errors.push({
              row: row.rowNumber,
              error: result.message || 'Failed to upload'
            });
          }
        } catch (error) {
          results.failed++;
          results.errors.push({
            row: row.rowNumber,
            error: error.message || 'Network error'
          });
        }
      }

      setUploadResults(results);
      setUploadStatus('success');
      
      if (results.successful > 0) {
        toast.success(`Successfully uploaded ${results.successful} cars!`);
      }
      if (results.failed > 0) {
        toast.warning(`${results.failed} cars failed to upload. Check errors below.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload cars. Please try again.');
      setUploadStatus('idle');
    }
  };

  const removeFile = () => {
    setFile(null);
    setUploadStatus('idle');
    setUploadResults(null);
    setParsedData([]);
    setShowPreview(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    // Create a sample Excel template
    const templateData = [
      {
        Brand: 'Maruti Suzuki',
        Model: 'Swift',
        Year: 2020,
        Price: 550000,
        FuelType: 'Petrol',
        Transmission: 'Manual',
        'KM Driven': 25000,
        Mileage: 18.5,
        Location: 'Mumbai, Maharashtra',
        Description: 'Well maintained car in excellent condition',
        Seller: 'John Doe',
        Color: 'White',
        Features: 'Air Conditioning, Power Steering, ABS, Airbags'
      },
      {
        Brand: 'Hyundai',
        Model: 'i20',
        Year: 2019,
        Price: 600000,
        FuelType: 'Diesel',
        Transmission: 'Automatic',
        'KM Driven': 30000,
        Mileage: 20.5,
        Location: 'Delhi, Delhi',
        Description: 'Single owner, full service history',
        Seller: 'Jane Smith',
        Color: 'Red',
        Features: 'Air Conditioning, Power Windows, Sunroof, Bluetooth'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Cars');
    
    // Set column widths
    const wscols = [
      { wch: 15 }, // Brand
      { wch: 15 }, // Model
      { wch: 8 },  // Year
      { wch: 12 }, // Price
      { wch: 12 }, // FuelType
      { wch: 15 }, // Transmission
      { wch: 12 }, // KM Driven
      { wch: 10 }, // Mileage
      { wch: 25 }, // Location
      { wch: 40 }, // Description
      { wch: 15 }, // Seller
      { wch: 12 }, // Color
      { wch: 50 }  // Features
    ];
    worksheet['!cols'] = wscols;

    XLSX.writeFile(workbook, 'bulk_upload_template.xlsx');
    toast.success('Template downloaded successfully!');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-4">
          <Link to="/dashboard/cars">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <FiArrowLeft className="h-5 w-5" />
            </motion.button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bulk Upload Cars</h1>
            <p className="mt-2 text-gray-600">Upload multiple cars using Excel spreadsheet</p>
          </div>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={downloadTemplate}
          className="inline-flex items-center px-4 py-2 border border-orange-300 text-orange-700 bg-orange-50 font-medium rounded-xl hover:bg-orange-100 transition-all duration-200"
        >
          <FiDownload className="mr-2 h-4 w-4" />
          Download Template
        </motion.button>
      </motion.div>

      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-blue-50 border border-blue-200 rounded-xl p-6"
      >
        <h3 className="text-lg font-semibold text-blue-900 mb-4">Upload Instructions</h3>
        <div className="space-y-2 text-blue-800">
          <p>• Download the Excel template using the button above</p>
          <p>• Fill in all required fields: Brand, Model, Year, Price, Fuel Type, Transmission</p>
          <p>• Optional fields: KM Driven, Location, Description, Seller Name</p>
          <p>• Ensure all prices are in rupees (numbers only)</p>
          <p>• Upload the completed file using the upload area below</p>
        </div>
      </motion.div>

      {/* Upload Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl p-8 card-shadow"
      >
        {!file ? (
          <div className="flex items-center justify-center w-full">
            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <FiUpload className="w-12 h-12 mb-4 text-gray-500" />
                <p className="mb-2 text-lg text-gray-700 font-medium">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-sm text-gray-500">Excel files only (.xlsx, .xls)</p>
                <p className="text-xs text-gray-400 mt-2">Maximum file size: 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-6">
            {/* File Info */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiFileText className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{file.name}</p>
                  <p className="text-sm text-gray-500">
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={removeFile}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <FiX className="h-5 w-5" />
              </motion.button>
            </div>

            {/* Upload Button */}
            <div className="flex justify-center gap-4">
              {showPreview && parsedData.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowPreview(!showPreview)}
                  className="inline-flex items-center px-6 py-3 border border-orange-300 text-orange-700 bg-orange-50 font-medium rounded-xl hover:bg-orange-100 transition-all duration-200"
                >
                  <FiEye className="mr-2 h-5 w-5" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </motion.button>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleUpload}
                disabled={uploadStatus === 'uploading' || parsedData.filter(d => d.valid).length === 0}
                className={`inline-flex items-center px-8 py-3 font-medium rounded-xl transition-all duration-200 ${
                  uploadStatus === 'uploading' || parsedData.filter(d => d.valid).length === 0
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'gradient-bg text-white shadow-lg hover:shadow-xl'
                }`}
              >
                {uploadStatus === 'uploading' ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Uploading {parsedData.filter(d => d.valid).length} cars...
                  </>
                ) : (
                  <>
                    <FiUpload className="mr-2 h-5 w-5" />
                    Upload {parsedData.filter(d => d.valid).length} Valid Cars
                  </>
                )}
              </motion.button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Data Preview */}
      {showPreview && parsedData.length > 0 && !uploadResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Data Preview</h3>
            <div className="flex gap-4">
              <span className="px-3 py-1 text-sm font-medium text-green-600 bg-green-50 rounded-full">
                {parsedData.filter(d => d.valid).length} Valid
              </span>
              <span className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-full">
                {parsedData.filter(d => !d.valid).length} Invalid
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Row</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Brand</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Model</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Year</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Fuel</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Transmission</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Color</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {parsedData.map((row, index) => (
                  <tr key={index} className={row.valid ? 'bg-white hover:bg-gray-50' : 'bg-red-50'}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.rowNumber}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.brand}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.model}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.year}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">₹{row.price?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.fuelType}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.transmission}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.color || '-'}</td>
                    <td className="px-4 py-3 text-sm">
                      {row.valid ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                          <FiCheckCircle className="mr-1 h-3 w-3" />
                          Valid
                        </span>
                      ) : (
                        <div className="group relative">
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full cursor-help">
                            <FiAlertCircle className="mr-1 h-3 w-3" />
                            Invalid
                          </span>
                          <div className="hidden group-hover:block absolute z-10 w-64 p-2 mt-1 text-xs text-white bg-gray-900 rounded-lg shadow-lg">
                            {row.errors.map((err, i) => (
                              <div key={i}>• {err}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsedData.filter(d => !d.valid).length > 0 && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-red-900 mb-2">Invalid Rows Found</h4>
                  <p className="text-sm text-red-800">
                    {parsedData.filter(d => !d.valid).length} rows have errors. Hover over the Invalid badge to see details.
                    Only valid rows will be uploaded.
                  </p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Upload Results */}
      {uploadResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Summary */}
          <div className="bg-white rounded-2xl p-6 card-shadow">
            <div className="flex items-center space-x-3 mb-6">
              <FiCheckCircle className="h-6 w-6 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Upload Complete</h3>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <p className="text-3xl font-bold text-blue-600">{uploadResults.total}</p>
                <p className="text-sm text-blue-800">Total Rows</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-xl">
                <p className="text-3xl font-bold text-green-600">{uploadResults.successful}</p>
                <p className="text-sm text-green-800">Successfully Added</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-xl">
                <p className="text-3xl font-bold text-red-600">{uploadResults.failed}</p>
                <p className="text-sm text-red-800">Failed</p>
              </div>
            </div>
          </div>

          {/* Errors */}
          {uploadResults.errors.length > 0 && (
            <div className="bg-white rounded-2xl p-6 card-shadow">
              <div className="flex items-center space-x-3 mb-4">
                <FiAlertCircle className="h-5 w-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Errors Found</h3>
              </div>
              <div className="space-y-3">
                {uploadResults.errors.map((error, index) => (
                  <div key={index} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800">
                      <span className="font-medium">Row {error.row}:</span> {error.error}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setFile(null);
                setUploadStatus('idle');
                setUploadResults(null);
                setParsedData([]);
                setShowPreview(false);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200"
            >
              Upload Another File
            </motion.button>
            <Link to="/dashboard/cars">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center px-6 py-3 gradient-bg text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                View All Cars
              </motion.button>
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BulkUpload;